<template>
  <v-dialog
    :value="tagId != null"
    max-width="600px"
    persistent
    @keydown.esc="done(false)"
  >
    <v-card v-if="msa" :loading="$apollo.loading">
      <v-card-title>
        <v-row>
          <v-col>
            <span>{{ $t("mentorship.actions.title") }} </span>
          </v-col>
          <v-col cols="auto">
            <v-btn icon @click="done(false)">
              <v-icon>close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-form ref="msaMainForm">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="msa.tagId"
                :items="tags"
                required
                chips
                :item-text="getTagText"
                item-value="id"
                deletable-chips
                :rules="[rules.required]"
                :label="$t('mentorship.actions.column.tag')"
              >
                <template v-slot:item="{ item, attrs }">
                  <v-list-item-action>
                    <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      getTagText(item)
                    }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.explanation">
                      <span> {{ item.explanation }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <template v-if="msa.tagId && msa.version">
              <v-col cols="12">
                <v-text-field
                  v-model="msa.code"
                  :label="$t('mentorship.actions.column.code')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="msa.version.revision"
                  @change="handleRevisionChange"
                  :label="$t('mentorship.actions.column.revision')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="8">
                <v-text-field
                  :value="$options.filters.dateTime(msa.version.created)"
                  :disabled="true"
                  :label="$t('mentorship.actions.column.date')"
                />
              </v-col>
            </template>
          </v-row>

          <v-tabs
            fixed-tabs
            v-model="tab"
            class="pb-4"
            v-if="
              msa.version &&
                msa.version.revision &&
                msa.version.names &&
                msa.version.names.length &&
                msa.version.content &&
                msa.version.content.length
            "
          >
            <template v-for="(locale, index) in locales">
              <v-tab
                :key="index"
                @click="changeCurrentLocale(locale.value)"
                :tab="locale.value"
                >{{ locale.text }}

                <v-btn
                  v-if="!actionLocales.includes(locale.value)"
                  class="ml-3"
                  icon
                  @click.stop="addLocale(locale.value, index, msa.version.id)"
                >
                  <v-icon> add</v-icon>
                </v-btn>

                <v-btn
                  v-else
                  class="ml-3"
                  icon
                  :disabled="
                    actionLocales.length == 1 ||
                      !!msa.version.content
                        .filter((c) => c.language == locale.value)
                        .some((c) => c.id)
                  "
                  @click.stop="deleteLocale(locale.value)"
                >
                  <v-icon> delete_forever </v-icon>
                </v-btn>
              </v-tab>
            </template>
          </v-tabs>
          <v-tabs-items
            v-model="tab"
            v-if="
              msa.version &&
                msa.version.revision &&
                msa.version.names &&
                msa.version.names.length &&
                msa.version.content &&
                msa.version.content.length
            "
          >
            <template v-for="(locale, localeIndex) in locales">
              <v-tab-item eager :key="localeIndex">
                <template
                  v-for="(n, nameIndex) in msa.version.names.filter(
                    (n) => n.language == locale.value
                  )"
                >
                  <v-row :key="`n${nameIndex}`">
                    <v-alert
                      v-if="
                        maxContentLength !=
                          contentLengthByLanguage.get(locale.value)
                      "
                      dense
                      outlined
                      icon="error"
                      type="error"
                      style="width: 100%"
                      class="white-alert mx-2"
                      >{{
                        $t(
                          "mentorship.actions.edit.actionsLengthInLanguageMismatch"
                        )
                      }}
                    </v-alert>
                    <v-col cols="12">
                      <v-text-field
                        v-model="n.name"
                        :label="$t('mentorship.actions.column.name')"
                        :rules="[rules.required(n.name, localeIndex)]"
                      />
                    </v-col>
                  </v-row>
                </template>
                <template
                  v-for="(c, contentIndex) in msa.version.content.filter(
                    (c) => c.language == locale.value
                  )"
                >
                  <v-row :key="`c${contentIndex}`">
                    <mentorship-action-card-edit
                      v-if="c.isInEdit"
                      :action="c"
                      :actionIndex="contentIndex"
                      @isValidChange="
                        (isValid) => handleIsValidChange(isValid, c)
                      "
                      @save="(e) => saveAction(e, c)"
                      @close="
                        alert = c;
                        alert.callback = () => {
                          c.isInEdit = false;
                          alert = null;
                        };
                      "
                    />
                    <mentorship-action-card
                      v-else
                      :action="c"
                      :actionIndex="contentIndex"
                      :canDelete="!msa.version.id"
                      @delete="deleteAction(c)"
                      @edit="c.isInEdit = true"
                      @close="c.isInEdit = false"
                    />
                  </v-row>
                </template>

                <v-row dense class="my-4">
                  <v-btn
                    color="primary"
                    small
                    text
                    @click="addAction(locale.value, null, null, '', '')"
                    ><v-icon left>add</v-icon
                    >{{ $t("mentorship.actions.edit.add") }}</v-btn
                  >
                  <div style="padding-left: 14px; padding-right: 14px" />
                </v-row>
              </v-tab-item>
            </template>
          </v-tabs-items>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="done(false)">
          {{ $t("dialog.cancel") }}
        </v-btn>
        <v-btn color="primary" text @click="save">
          {{ $t("dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <alert
      :alert="alert"
      :title="$t('dialog.continueWithoutSaving')"
      :text="`${(alert && alert.title) || ''}`"
      :okText="$t('dialog.yes')"
      @cancel="alert = null"
      @ok="alert.callback()"
    />
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import UpsertMentorshipActionsQuery from "@/graphql/mutation/mentorshipActions/UpsertMentorshipActions.gql";
import TagsQuery from "@/graphql/Tags.gql";
import MentorshipActionByTagIdQuery from "@/graphql/queries/MentorshipActionByTagId.gql";

import MentorshipActionCardEdit from "@/components/mentorship/MentorshipActionCardEdit.vue";
import MentorshipActionCard from "@/components/mentorship/MentorshipActionCard.vue";
import Alert from "@/components/Alert.vue";

import { getTagText } from "@/utils/mentorshipActions";

export default {
  props: {
    tagId: {
      type: Number,
      default: null,
    },
  },
  components: {
    MentorshipActionCardEdit,
    MentorshipActionCard,
    Alert,
  },
  data() {
    return {
      alert: null,
      rules: {
        required: (value, localeIndex) => {
          const isValid = !!value;

          if (!isValid && localeIndex !== undefined) {
            this.appendNotValidTabs(localeIndex);
          }
          return isValid || this.$i18n.t("validation.required");
        },
      },
      show: true,
      msa: null,
      tab: null,
      notValidTabs: [],
    };
  },
  mounted() {
    this.msa = {
      tagId: this.tagId,
    };
  },
  computed: {
    ...mapGetters(["locales"]),
    actionLocales: {
      get() {
        return (
          (this.msa &&
            this.msa.version &&
            this.msa.version.content &&
            this.msa.version.content.length &&
            this.msa.version.content
              .map((c) => c.language)
              .filter((v, i, a) => a.indexOf(v) === i)) || [
            this.locales[0].value,
          ]
        );
      },
      set() {},
    },
    contentLengthByLanguage() {
      const contentLengthByLanguage = new Map();
      this.msa &&
        this.msa.version.content.forEach((c) => {
          if (contentLengthByLanguage.has(c.language)) {
            contentLengthByLanguage.set(
              c.language,
              contentLengthByLanguage.get(c.language) + 1
            );
          } else {
            contentLengthByLanguage.set(c.language, 1);
          }
        });

      return contentLengthByLanguage;
    },

    maxContentLength() {
      return Math.max(...this.contentLengthByLanguage.values());
    },
    currentLocale: {
      get() {
        return this.locales[0].value;
      },
      set(newVal) {
        return newVal;
      },
    },
  },
  methods: {
    handleRevisionChange() {
      this.msa.version.id = null;
      this.msa.version.created = new Date();

      if (this.msa.version.names && this.msa.version.names.length) {
        this.msa.version.names = this.msa.version.names.map((n) =>
          this.newName(n.language, null, null, n.name)
        );
      } else {
        this.addName(this.$i18n.locale, null, null, "");
      }

      if (this.msa.version.content && this.msa.version.content.length) {
        this.msa.version.content = this.msa.version.content.map((c) =>
          this.newAction(
            c.language,
            null,
            null,
            c.title,
            c.content,
            false,
            true
          )
        );
      } else {
        this.addAction(this.$i18n.locale, null, null, "", "");
        const tabIndex = this.locales.findIndex(
          (l) => l.value == this.$i18n.locale
        );
        if (tabIndex > -1) {
          this.tab = tabIndex;
        }
      }
    },

    done(updated = true) {
      this.$emit("done", updated);
    },

    validateFormAndCheckContent() {
      this.notValidTabs = [];
      const isFormValid = this.$refs.msaMainForm.validate();
      this.contentLengthByLanguage.forEach((count, language) => {
        if (count != this.maxContentLength) {
          this.appendNotValidTabs(
            this.locales.findIndex((l) => l.value == language)
          );
        }
      });
      this.msa.version.content.forEach((c) => {
        if (!c.isValid) {
          this.appendNotValidTabs(
            this.locales.findIndex((l) => l.value == c.language)
          );
        }
      });
      return isFormValid;
    },

    save() {
      const isFormValid = this.validateFormAndCheckContent();

      this.$nextTick(() => {
        if (!isFormValid || !!this.notValidTabs.length) {
          this.tab = this.notValidTabs[0];
          this.notValidTabs.splice(0, 1);

          return;
        }
        const firstInEdit = this.msa.version.content.find((c) => c.isInEdit);
        if (firstInEdit) {
          this.alert = firstInEdit;
          this.alert.callback = () => {
            this.alert.isInEdit = false;
            this.save();
          };
          return;
        }

        const input = this.remap(this.msa);
        this.$apollo
          .mutate({
            mutation: UpsertMentorshipActionsQuery,
            variables: {
              in: input,
            },
          })
          .then(() => {
            this.done();
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: `${this.$i18n.t("error.saveFailed")} ${err}`,
            });
          });
      });
    },
    handleIsValidChange(isValid, content) {
      if (typeof isValid != "boolean") {
        return;
      }
      content.isValid = isValid;
    },

    getTagText(tag) {
      return getTagText(tag);
    },
    deleteLocale(language) {
      const contentToDelete = this.msa.version.content.filter(
        (c) => c.language == language
      );
      contentToDelete.forEach((c) => this.deleteAction(c));
      this.deleteName(
        this.msa.version.names.find((n) => n.language == language)
      );

      this.actionLocales = this.actionLocales.filter((l) => l != language);
    },

    deleteAction(c) {
      this.$delete(
        this.msa.version.content,
        this.msa.version.content.indexOf(c)
      );
    },
    deleteName(n) {
      this.$delete(this.msa.version.names, this.msa.version.names.indexOf(n));
    },

    saveAction(newAction, action) {
      if (!newAction || !action) return;
      action.title = newAction.title;
      action.content = newAction.content;
      action.isInEdit = false;
    },

    addLocale(locale, tab, versionId) {
      if (this.actionLocales.includes(locale)) return;
      this.actionLocales.push(locale);
      this.addName(locale, null, versionId, "");
      this.addAction(locale, null, null, "", "");
      this.changeLocaleAndTab(locale, tab);
    },

    changeCurrentLocale(locale) {
      this.currentLocale = locale;
    },

    changeLocaleAndTab(locale, tab) {
      this.changeCurrentLocale(locale);
      this.tab = tab;
    },

    addName(language, id, versionId, name) {
      if (this.msa.version.names.find((n) => n.language == language)) return;
      this.msa.version.names.push(this.newName(language, id, versionId, name));
    },
    newName(language, id, versionId, name) {
      return {
        id: id || null,
        versionId: versionId || null,
        language: language,
        name: name || "",
      };
    },

    canAddAction(language) {
      if (!this.msa.version.id && this.actionLocales.includes(language)) {
        return true;
      }
      if (!this.actionLocales.includes(language)) {
        return false;
      }
      if (!this.contentLengthByLanguage.get(language)) {
        return true;
      }
      return this.maxContentLength > this.contentLengthByLanguage.get(language);
    },

    addAction(language, id, contentId, title, content) {
      if (!this.canAddAction(language)) {
        this.$store.commit("push_global_message", {
          color: "warning",
          text: this.$t("validation.revisionChangeNeeded"),
        });

        return;
      }
      this.msa.version.content.forEach((c) => (c.isInEdit = false));
      const newAction = this.newAction(
        language,
        id,
        contentId,
        title,
        content,
        true,
        false
      );

      this.msa.version.content.push(newAction);
    },

    newAction(language, id, contentId, title, content, isInEdit, isValid) {
      const obj = {
        id: id || null,
        contentId: contentId || null,
        language: language || this.currentLocale,
        title: title || "",
        content: content || "",
      };
      if (isInEdit !== undefined) {
        Object.assign(obj, { isInEdit });
      }
      if (isValid !== undefined) {
        Object.assign(obj, { isValid });
      }
      return obj;
    },

    appendNotValidTabs(localeIndex) {
      !this.notValidTabs.includes(localeIndex) &&
        this.notValidTabs.push(+localeIndex);
    },

    remap(ma, isInEdit, isValid) {
      return {
        id: ma.id,
        code: ma.code,
        tagId: ma.tagId,
        version: {
          mentorshipActionId: ma.id,
          id: ma.version.id,
          revision: ma.version.revision,
          created: ma.version.created,
          names: ma.version.names.map((n) =>
            this.newName(n.language, n.id, n.versionId, n.name)
          ),
          content: ma.version.content.map((c) =>
            this.newAction(
              c.language,
              c.id,
              c.contentId,
              c.title,
              c.content,
              isInEdit,
              isValid
            )
          ),
        },
      };
    },
  },
  apollo: {
    tags: {
      query: TagsQuery,
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.tags.filter((t) =>
          ["position", "department", "location"].includes(t.category)
        );
      },
    },
    mentorshipAction: {
      query: MentorshipActionByTagIdQuery,
      variables() {
        return {
          tagId: this.msa.tagId,
        };
      },
      skip() {
        return this.msa == null || (this.msa && this.msa.tagId == null);
      },
      update(data) {
        if (data.mentorshipAction) {
          this.msa = this.remap(data.mentorshipAction, false, true);
          return;
        }

        // not found
        this.msa = {
          code: "",
          tagId: this.msa.tagId,
          version: {
            revision: "",
            created: new Date(),
            names: [],
            content: [],
          },
        };

        return data.mentorshipAction;
      },
    },
  },
};
</script>
